import { useEffect, useMemo, useState } from "react";
import { AiOutlineClose } from "react-icons/ai";
import { FaMinus, FaPlus } from "react-icons/fa";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import ReactSlidingPane from "react-sliding-pane";
// Imported here, next to the component that renders the pane. It used to be
// pulled in only by a legacy header that has since left the bundle, which took
// the pane's positioning/overlay styles with it.
import "react-sliding-pane/dist/react-sliding-pane.css";
import { toast } from "react-toastify";
import {
  addItem,
  decreaseItem,
  removeItem,
} from "../../Redux/State-slice/CartSlice";
import currencyLabels from "../../translationData/currency.json";
import { getStoredUser } from "../../services/auth.api";
import { effectivePrice, hasDiscount, lineTotal } from "../../utils/price";
import { FOOD_TYPE, foodTypeLabel } from "../../services/normalizers";
import placeholderImg from "../../assets/placeholder-image.png";

const sym = () =>
  currencyLabels.country === "Bangladesh"
    ? currencyLabels.currency.bdt.symbol
    : currencyLabels.currency.usa.symbol;

const DrawerRight = ({ cart = [], state = {}, setState }) => {
  const [checkoutLoadingSellerId, setCheckoutLoadingSellerId] = useState(null);

  const User = getStoredUser();

  const removeCartData = (id) => {
    dispatch(removeItem(id));
  };

  useEffect(() => {
    localStorage.setItem("cartList", JSON.stringify(cart));
  }, [cart]);
  const increaseQty = (item) => {
    dispatch(addItem(item));
  };

  const decreaseQty = (item) => {
    dispatch(decreaseItem(item));
  };
  const dispatch = useDispatch();

  const navigate = useNavigate();
  const goToCheckoutBySeller = (sellerId) => {
    setState({ isPaneOpen: false });
    navigate("/CheckOut", { state: { sellerId } });
  };

  const checkOutBySeller = async (sellerItems, sellerId) => {
    if (!sellerItems?.length) {
      toast.warning("No item found for checkout.");
      return;
    }

    if (!User) {
      navigate("/authentication/login");
      return;
    }

    // This only opens the checkout page — nothing is ordered yet, so it must
    // not announce success.
    setCheckoutLoadingSellerId(sellerId);
    localStorage.setItem("cartList", JSON.stringify(cart));
    goToCheckoutBySeller(sellerId);
    setCheckoutLoadingSellerId(null);
  };
  /**
   * Grouped by kitchen: the API accepts only one chef per order, so each group
   * checks out separately.
   */
  const groupedCart = useMemo(() => {
    const groups = {};
    cart?.forEach((item) => {
      const key = item.sellerID || item.sellerId?._id || "unknown";
      if (!groups[key]) {
        groups[key] = {
          sellerName:
            item.sellerId?.name || item.sellerId?.kitchenName || "Unknown Kitchen",
          sellerId: key,
          items: [],
          subTotal: 0,
        };
      }
      groups[key].items.push(item);
      groups[key].subTotal += lineTotal(item);
    });
    return Object.values(groups);
  }, [cart]);

  return (
    <div>
      <ReactSlidingPane
        className="some-custom-class"
        overlayClassName="some-custom-overlay-class"
        isOpen={state.isPaneOpen}
        width="400px"
        onRequestClose={() => setState({ isPaneOpen: false })}
      >
        <aside className="cart-sidebar active">
          <div className="cart-header">
            <div className="cart-total">
              <i className="fas fa-shopping-basket"></i>
              <span>total item ({cart?.length})</span>
            </div>

            <button
              className="cart-close"
              onClick={() => {
                setState({ isPaneOpen: false });
              }}
            >
              <span className="icofont-close">
                <AiOutlineClose />
              </span>
            </button>
          </div>
          <div className="p-1">
            {groupedCart.map((group) => (
              <div key={group.sellerId} className="drawer-seller-card">
                <div className="drawer-seller-header d-flex align-items-center justify-content-between">
                  <h2 className="drawer-seller-name">{group.sellerName}</h2>
                  <span className="drawer-seller-total">
                    {sym()}
                    {group.subTotal?.toFixed(0)}
                  </span>
                </div>
                {group.items.map((item, index) => (
                  <div
                    className="cart-item drawer-cart-item"
                    key={item._id || index}
                  >
                    <div className="cart-media">
                      <Link to={`/food-details/${item?._id}`}>
                        {/* Every other food card falls back; this one didn't,
                            so a missing or dead image left an empty box. */}
                        <img
                          src={item?.image || placeholderImg}
                          alt={item?.name || "Food"}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = placeholderImg;
                          }}
                        />
                      </Link>
                      <button
                        className="cart-delete"
                        onClick={() => removeCartData(item?._id)}
                      >
                        <i className="far fa-trash-alt"></i>
                      </button>
                    </div>
                    <div className="cart-info-group">
                      <div className="cart-info d-lg-flex justify-content-between align-content-start">
                        <div>
                          <h6>
                            <Link to={`/food-details/${item?._id}`}>
                              {item?.name}
                            </Link>
                          </h6>

                          <div className="d-flex align-items-center gap-2">
                            <p>
                              {sym()}
                              {effectivePrice(item).toFixed(0)}
                            </p>
                            {hasDiscount(item) && (
                              <p className="text-decoration-line-through text-danger size-12">
                                {sym()}
                                {Number(item?.price).toFixed(0)}
                              </p>
                            )}
                          </div>
                          {/* Per item, not "whatever happens to be first in the cart". */}
                          {item?.type === FOOD_TYPE.PRE_ORDER && item?.waitingTime && (
                            <p>Ready in {item.waitingTime}</p>
                          )}
                        </div>
                        <div>
                          <span
                            className="text-bold"
                            style={{ fontSize: "12px" }}
                          >
                            {foodTypeLabel(item.type)}
                          </span>
                        </div>
                      </div>
                      <div className="cart-action-group">
                        <div className="product-action">
                          <button
                            onClick={() => decreaseQty(item)}
                            className="action-minus"
                            title="Quantity Minus"
                          >
                            <FaMinus />
                          </button>

                          <span
                            className="action-input ps-1 pe-1 rounded"
                            style={{ backgroundColor: "#ead4d44f" }}
                          >
                            {item.foodQty}
                          </span>

                          <button
                            onClick={() => increaseQty(item)}
                            className="action-plus"
                            title="Quantity Plus"
                          >
                            <FaPlus />
                          </button>
                        </div>
                        <div>
                          <h6>
                            {sym()}
                            {lineTotal(item).toFixed(0)}
                          </h6>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                <div className="d-flex align-items-center gap-2 mt-2 drawer-seller-action">
                  <button
                    type="button"
                    className="w-100 cart-checkout-btn border-0 mt-0 seller-checkout-btn"
                    onClick={() =>
                      checkOutBySeller(group.items, group.sellerId)
                    }
                    disabled={checkoutLoadingSellerId === group.sellerId}
                  >
                    <span className="checkout-label">
                      {checkoutLoadingSellerId === group.sellerId
                        ? "Processing..."
                        : `Proceed to Checkout (${group.items.length})`}
                    </span>
                    <span className="checkout-price">
                      {sym()}
                      {group.subTotal?.toFixed(0)}
                    </span>
                  </button>
                </div>
              </div>
            ))}
            {groupedCart.length === 0 && (
              <p className="text-center mt-3">No items in cart</p>
            )}
          </div>
        </aside>
      </ReactSlidingPane>
    </div>
  );
};

export default DrawerRight;
